// Design tokens handed to widgets in `momentum:init.context.tokens` so third-party UI can
// match the host without hard-coding colours. Values are read from the live CSS variables
// in styles/theme.css, so they follow the active light/dark theme automatically.

export type WidgetDesignTokens = Record<string, string>;

// token name → CSS custom property (with a fallback for environments without the theme).
const TOKEN_SOURCES: Array<[string, string, string]> = [
  ['accent', '--accent-purple', '#A200FF'],
  ['accentDeep', '--accent-purple-deep', '#381574'],
  ['accentLight', '--accent-purple-light', '#a78bfa'],
  ['background', '--bg-primary', '#0C0C0C'],
  ['backgroundSecondary', '--bg-secondary', '#110e2e'],
  ['surface', '--surface-card', 'rgba(255, 255, 255, 0.042)'],
  ['surfaceElevated', '--surface-elevated', 'rgba(255, 255, 255, 0.055)'],
  ['surfaceInput', '--surface-input', 'rgba(255, 255, 255, 0.03)'],
  ['border', '--border-glass', 'rgba(255, 255, 255, 0.08)'],
  ['borderSubtle', '--border-subtle', 'rgba(255, 255, 255, 0.05)'],
  ['text', '--text-primary', '#FAFAFA'],
  ['textSecondary', '--text-secondary', 'rgba(255, 255, 255, 0.58)'],
  ['textMuted', '--text-muted', 'rgba(255, 255, 255, 0.22)'],
  ['success', '--accent-green', '#34d399'],
  ['warning', '--accent-yellow', '#fbbf24'],
  ['danger', '--accent-red', '#ef4444'],
  ['info', '--accent-cyan', '#22d3ee'],
  ['radiusSm', '--radius-sm', '8px'],
  ['radius', '--radius-md', '12px'],
  ['radiusLg', '--radius-lg', '16px'],
];

const STATIC_TOKENS: WidgetDesignTokens = {
  fontFamily: "'Poppins', system-ui, sans-serif",
  brandPurple: '#381574',
  brandRoyal: '#A200FF',
};

/** Snapshot of the host's design tokens for the current theme. Cheap enough to call on every init. */
export function readDesignTokens(): WidgetDesignTokens {
  const tokens: WidgetDesignTokens = { ...STATIC_TOKENS };
  let styles: CSSStyleDeclaration | null = null;
  try { styles = getComputedStyle(document.documentElement); } catch { styles = null; }
  for (const [name, cssVar, fallback] of TOKEN_SOURCES) {
    const value = styles?.getPropertyValue(cssVar).trim();
    tokens[name] = value || fallback;
  }
  return tokens;
}

/** The viewer's locale settings, so widgets format dates/currency the way the user expects. */
export function readLocaleContext(): { locale: string; timeZone: string; currency: string } {
  let timeZone = 'UTC';
  try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { /* keep default */ }
  return {
    locale: (typeof navigator !== 'undefined' && navigator.language) || 'en-US',
    timeZone,
    currency: 'USD',
  };
}
